import React, { useEffect, useRef } from 'react'
import { gsap, ScrollTrigger } from 'gsap/all'
import { AURORA_SEEDS, getSeedIndex } from './seeds'

gsap.registerPlugin(ScrollTrigger)

const MAX_INFLUENCE_RADIUS = 850

interface AuroraBlobsProps {
  seed?: number | string
  containerRef: React.RefObject<HTMLDivElement>
  parallaxRef: React.RefObject<HTMLDivElement>
}

export const AuroraBlobs: React.FC<AuroraBlobsProps> = ({
  seed,
  containerRef,
  parallaxRef,
}) => {
  const attractor1Ref = useRef<HTMLDivElement>(null)
  const attractor2Ref = useRef<HTMLDivElement>(null)
  const attractor3Ref = useRef<HTMLDivElement>(null)
  const attractor4Ref = useRef<HTMLDivElement>(null)
  const blob1Ref = useRef<HTMLDivElement>(null)
  const blob2Ref = useRef<HTMLDivElement>(null)
  const blob3Ref = useRef<HTMLDivElement>(null)
  const blob4Ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (typeof window === 'undefined' || !parallaxRef.current || !containerRef.current) return

    const triggerElement = containerRef.current.parentElement || containerRef.current

    // Parallax scroll tween - pure GPU
    const parallaxTween = gsap.to(parallaxRef.current, {
      y: 160,
      ease: 'none',
      scrollTrigger: {
        trigger: triggerElement,
        start: 'top top',
        end: 'bottom top',
        scrub: 1.2,
      },
    })

    const selectedSeedIdx = getSeedIndex(seed)
    const currentSeedConfig = AURORA_SEEDS[selectedSeedIdx]

    // Pure GPU transform tween for loose, ambient morphing (no layout reflows)
    const animateBlob = (
      el: HTMLElement | null,
      config: { minX: number; maxX: number; minY: number; maxY: number }
    ) => {
      if (!el) return

      const targetX = gsap.utils.random(config.minX, config.maxX, 1)
      const targetY = gsap.utils.random(config.minY, config.maxY, 1)
      const targetScaleX = gsap.utils.random(0.8, 1.35)
      const targetScaleY = gsap.utils.random(0.8, 1.35)
      const targetRotation = gsap.utils.random(-220, 220, 1)
      const targetDuration = gsap.utils.random(3.2, 5.5)

      gsap.to(el, {
        x: targetX,
        y: targetY,
        scaleX: targetScaleX,
        scaleY: targetScaleY,
        rotation: targetRotation,
        duration: targetDuration,
        ease: 'sine.inOut',
        force3D: true,
        onComplete: () => {
          animateBlob(el, config)
        },
      })
    }

    const attractorRefs = [attractor1Ref, attractor2Ref, attractor3Ref, attractor4Ref]
    const blobRefs = [blob1Ref, blob2Ref, blob3Ref, blob4Ref]

    const attractorStates = [
      { currentX: 0, currentY: 0, targetX: 0, targetY: 0, lerp: 0.05, maxPull: 220 },
      { currentX: 0, currentY: 0, targetX: 0, targetY: 0, lerp: 0.042, maxPull: 250 },
      { currentX: 0, currentY: 0, targetX: 0, targetY: 0, lerp: 0.055, maxPull: 200 },
      { currentX: 0, currentY: 0, targetX: 0, targetY: 0, lerp: 0.046, maxPull: 240 },
    ]

    let mouseX = -9999
    let mouseY = -9999
    let isMouseActive = false

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      mouseX = e.clientX - rect.left
      mouseY = e.clientY - rect.top
      isMouseActive = true
    }

    const handleMouseLeave = () => {
      isMouseActive = false
    }

    window.addEventListener('mousemove', handleMouseMove, { passive: true })
    document.addEventListener('mouseleave', handleMouseLeave)

    const tickerUpdate = () => {
      if (!containerRef.current) return

      attractorRefs.forEach((ref, idx) => {
        const el = ref.current
        if (!el) return
        const state = attractorStates[idx]

        if (isMouseActive) {
          const bCenterX = el.offsetLeft + el.offsetWidth / 2 + state.currentX
          const bCenterY = el.offsetTop + el.offsetHeight / 2 + state.currentY

          const dx = mouseX - bCenterX
          const dy = mouseY - bCenterY
          const dist = Math.sqrt(dx * dx + dy * dy)

          if (dist < MAX_INFLUENCE_RADIUS) {
            const proximity = 1 - dist / MAX_INFLUENCE_RADIUS
            // Non-linear attraction: smooth ease-in as cursor approaches
            const factor = Math.pow(proximity, 1.35)
            const pull = factor * state.maxPull
            const angle = Math.atan2(dy, dx)

            state.targetX = Math.cos(angle) * pull
            state.targetY = Math.sin(angle) * pull
          } else {
            state.targetX = 0
            state.targetY = 0
          }
        } else {
          state.targetX = 0
          state.targetY = 0
        }

        // Smooth physics lerp
        state.currentX += (state.targetX - state.currentX) * state.lerp
        state.currentY += (state.targetY - state.currentY) * state.lerp

        gsap.set(el, {
          x: state.currentX,
          y: state.currentY,
          force3D: true,
        })
      })
    }

    gsap.ticker.add(tickerUpdate)

    currentSeedConfig.blobs.forEach((blobConfig, idx) => {
      const attractorEl = attractorRefs[idx]?.current
      const blobEl = blobRefs[idx]?.current
      if (!attractorEl || !blobEl) return

      if (blobConfig.initialPlacement) {
        if (blobConfig.initialPlacement.top !== undefined) attractorEl.style.top = blobConfig.initialPlacement.top
        if (blobConfig.initialPlacement.bottom !== undefined) attractorEl.style.bottom = blobConfig.initialPlacement.bottom
        if (blobConfig.initialPlacement.left !== undefined) {
          attractorEl.style.left = blobConfig.initialPlacement.left
          attractorEl.style.right = 'auto'
        }
        if (blobConfig.initialPlacement.right !== undefined) {
          attractorEl.style.right = blobConfig.initialPlacement.right
          attractorEl.style.left = 'auto'
        }
        if (blobConfig.initialPlacement.width !== undefined) attractorEl.style.width = blobConfig.initialPlacement.width
        if (blobConfig.initialPlacement.height !== undefined) attractorEl.style.height = blobConfig.initialPlacement.height
      }

      // Random starting offset within seed range so blobs start at distinct locations immediately
      const startX = gsap.utils.random(blobConfig.bounds.minX, blobConfig.bounds.maxX, 1)
      const startY = gsap.utils.random(blobConfig.bounds.minY, blobConfig.bounds.maxY, 1)
      const startScaleX = gsap.utils.random(0.85, 1.25)
      const startScaleY = gsap.utils.random(0.85, 1.25)
      const startRotation = gsap.utils.random(-180, 180, 1)

      gsap.set(blobEl, {
        x: startX,
        y: startY,
        scaleX: startScaleX,
        scaleY: startScaleY,
        rotation: startRotation,
      })

      animateBlob(blobEl, blobConfig.bounds)
    })

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseleave', handleMouseLeave)
      gsap.ticker.remove(tickerUpdate)
      parallaxTween.scrollTrigger?.kill()
      parallaxTween.kill()
      gsap.killTweensOf([
        attractor1Ref.current,
        attractor2Ref.current,
        attractor3Ref.current,
        attractor4Ref.current,
        blob1Ref.current,
        blob2Ref.current,
        blob3Ref.current,
        blob4Ref.current,
      ])
    }
  }, [seed, containerRef, parallaxRef])

  return (
    <div
      className="absolute inset-0 filter blur-[45px] md:blur-[60px]"
      style={{ transform: 'translate3d(0,0,0)', willChange: 'transform' }}
    >
      {/* Aurora Blob 1 */}
      <div
        ref={attractor1Ref}
        className="absolute -top-8 left-[3%] w-[500px] h-[500px] md:w-[720px] md:h-[720px] rounded-full will-change-transform pointer-events-none"
      >
        <div
          ref={blob1Ref}
          className="aurora-blob-1 w-full h-full rounded-full will-change-transform"
        />
      </div>

      {/* Aurora Blob 2 */}
      <div
        ref={attractor2Ref}
        className="absolute top-[4%] right-[3%] w-[500px] h-[500px] md:w-[720px] md:h-[720px] rounded-full will-change-transform pointer-events-none"
      >
        <div
          ref={blob2Ref}
          className="aurora-blob-2 w-full h-full rounded-full will-change-transform"
        />
      </div>

      {/* Aurora Blob 3 */}
      <div
        ref={attractor3Ref}
        className="absolute top-[28%] left-[18%] w-[460px] h-[460px] md:w-[680px] md:h-[680px] rounded-full will-change-transform pointer-events-none"
      >
        <div
          ref={blob3Ref}
          className="aurora-blob-3 w-full h-full rounded-full will-change-transform"
        />
      </div>

      {/* Aurora Blob 4 */}
      <div
        ref={attractor4Ref}
        className="absolute top-[22%] right-[16%] w-[480px] h-[480px] md:w-[700px] md:h-[700px] rounded-full will-change-transform pointer-events-none"
      >
        <div
          ref={blob4Ref}
          className="aurora-blob-4 w-full h-full rounded-full will-change-transform"
        />
      </div>
    </div>
  )
}
